'use client';

import { useTracker } from '@lichtblick/tracker';
import { type FC, type FormHTMLAttributes, type SubmitEventHandler, useCallback, useRef } from 'react';
import { debounce, type FieldElement, getFormError } from './form.helpers';

interface FormProps extends Omit<FormHTMLAttributes<HTMLFormElement>, 'action'> {
  dispatch: (formData: FormData) => void;
  trackingName: string;
}

export const Form: FC<FormProps> = ({ children, dispatch, trackingName, onInvalid, ...props }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { trackCustomEvent } = useTracker();

  const handleFieldError = useCallback<SubmitEventHandler<HTMLFormElement>>(
    (event) => {
      const invalidFields = formRef.current?.querySelectorAll<FieldElement>(':is(input, select, textarea):invalid');
      const error = getFormError(invalidFields);

      invalidFields?.[0]?.focus();
      trackCustomEvent({ eventName: 'form_failed', customProps: { form_name: trackingName, error_message: error } });
      onInvalid?.(event);
    },
    [onInvalid, trackCustomEvent, trackingName],
  );

  const handleFailedSubmit = useCallback<SubmitEventHandler<HTMLFormElement>>(
    (event) => debounce(handleFieldError, undefined, event),
    [handleFieldError],
  );

  return (
    <form {...props} action={dispatch} onInvalid={handleFailedSubmit} ref={formRef}>
      {children}
    </form>
  );
};
